import { useState } from "react"
import type { UseFormReturn } from "react-hook-form"

import useHandleFileUpload from "@/components/upload/hooks/useHandleFileUpload"
import { ReferenceTableName } from "@/graphql/codegen/graphql"
import { ALLOWED_FILE_FORMATS } from "@/screens/Companies/edit/components/info/constants"
import type { CompanyFileFormValues } from "@/screens/Companies/edit/components/info/schema/CompanyFileSchema"

const useCompanyFileDialogActions = ({
  onSuccess,
  onClose,
  methods,
}: {
  onSuccess: (() => any) | undefined
  onClose: () => void
  methods: UseFormReturn<CompanyFileFormValues>
}): {
  setIsCancelConfirmationOpen: (isOpen: boolean) => void
  isCancelConfirmationOpen: boolean
  handleSubmit: () => Promise<void>
  canSubmit: boolean
  handleCancel: () => void
} => {
  const {
    handleSubmit: zodHandleSubmit,
    formState: { isValid, isDirty, errors, isSubmitting },
  } = methods

  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const { uploadFile } = useHandleFileUpload({
    referenceTableName: ReferenceTableName.Company,
    allowedFileTypes: ALLOWED_FILE_FORMATS,
  })

  const handleSubmit = zodHandleSubmit(async (input: CompanyFileFormValues) => {
    await uploadFile({
      file: input.file!,
      referenceId: input.companyId,
      type: input.fileType!,
    })
    onSuccess?.()
  })

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting
  return {
    setIsCancelConfirmationOpen,
    isCancelConfirmationOpen,
    canSubmit,
    handleSubmit,
    handleCancel,
  }
}

export default useCompanyFileDialogActions
